import React, { useState, useEffect } from 'react';
import { Pie } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
} from 'chart.js';
import { Card, CardContent, Typography, CircularProgress, Box, Grid, Button, IconButton, makeStyles, useTheme, TextField, FormControl, InputLabel, Select, MenuItem } from '@material-ui/core';
import AuthApi from '../../Services/Authapi';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';

ChartJS.register(
    ArcElement,
    Tooltip,
    Legend
);

const useStyles = makeStyles((theme) => ({
    card: {
        backgroundColor: theme.palette.type === 'dark' ? '#1e1e1e' : '#fff',
        color: theme.palette.text.primary,
        borderRadius: '12px',
        overflow: 'hidden',
        height: '100%',
        boxShadow: theme.shadows[2],
    },
    header: {
        padding: '10px 20px',
        textAlign: 'center',
    },
    headerTitle: {
        color: '#fff',
        fontWeight: 500,
        fontSize: '1.1rem',
    },
    toggleContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '20px',
        marginBottom: '20px',
    },
    toggleButton: {
        backgroundColor: theme.palette.type === 'dark' ? '#2f3b52' : '#e6eef7',
        color: theme.palette.type === 'dark' ? '#d7e3f4' : '#355070',
        padding: '8px 20px',
        fontSize: '0.8rem',
        fontWeight: 'bold',
        textTransform: 'none',
        borderRadius: '0',
        '&:first-child': {
            borderTopLeftRadius: '20px',
            borderBottomLeftRadius: '20px',
        },
        '&:last-child': {
            borderTopRightRadius: '20px',
            borderBottomRightRadius: '20px',
        },
        '&:hover': {
            backgroundColor: theme.palette.type === 'dark' ? '#3d4d6a' : '#d7e5f4',
        },
    },
    activeButton: {
        background: 'linear-gradient(180deg, var(--hrms-primary-blue-start, #036dda) 0%, var(--hrms-primary-blue-end, #036dda) 100%) !important',
        border: '1px solid var(--hrms-primary-blue-border, #036dda) !important',
        color: '#fff !important',
        '&:hover': {
            background: 'linear-gradient(180deg, #036dda 0%, #036dda 100%) !important',
        },
    },
    filterContainer: {
        display: 'flex',
        justifyContent: 'center',
        gap: '10px',
        marginBottom: '20px',
    },
    filterButton: {
        color: theme.palette.type === 'dark' ? '#d7e3f4' : '#355070',
        borderColor: theme.palette.type === 'dark' ? '#5f7697' : '#a7bfd8',
        fontSize: '0.75rem',
        padding: '4px 10px',
        textTransform: 'none',
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(95, 118, 151, 0.18)' : '#f8fbff',
        '&:hover': {
            borderColor: theme.palette.type === 'dark' ? '#8ea7c8' : '#6f9fca',
            color: theme.palette.type === 'dark' ? '#ffffff' : '#1f3f5b',
            backgroundColor: theme.palette.type === 'dark' ? 'rgba(95, 118, 151, 0.28)' : '#edf5fd',
        },
    },
    activeFilterButton: {
        color: '#036dda',
        borderColor: '#036dda',
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(3, 109, 218, 0.2)' : 'rgba(3, 109, 218, 0.16)',
        '&:hover': {
            borderColor: '#036dda',
            backgroundColor: theme.palette.type === 'dark' ? 'rgba(3, 109, 218, 0.28)' : 'rgba(3, 109, 218, 0.22)',
        },
    },
    dateSelector: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '15px',
        marginBottom: '20px',
        overflowX: 'auto',
        paddingBottom: '10px',
        paddingTop: '10px',
        '&::-webkit-scrollbar': {
            height: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.type === 'dark' ? '#555' : '#ccc',
            borderRadius: '2px',
        },
    },
    dateCircle: {
        width: '35px',
        height: '35px',
        borderRadius: '50%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: theme.palette.type === 'dark' ? '#2f3b52' : '#eef4fa',
        color: theme.palette.type === 'dark' ? '#d7e3f4' : '#355070',
        fontSize: '0.9rem',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'all 0.3s',
    },
    activeDateCircle: {
        background: 'linear-gradient(180deg, var(--hrms-primary-blue-start, #036dda) 0%, var(--hrms-primary-blue-end, #036dda) 100%)',
        border: '1px solid var(--hrms-primary-blue-border, #036dda)',
        color: '#fff',
        transform: 'scale(1.1)',
        boxShadow: '0 6px 14px rgba(3, 109, 218, 0.28)',
    },
    dateLabel: {
        fontSize: '0.65rem',
        color: theme.palette.type === 'dark' ? '#b4c5dc' : '#7b91a8',
        textAlign: 'center',
        marginTop: '5px',
        textTransform: 'uppercase',
    },
    activeDateLabel: {
        color: '#036dda',
        fontWeight: 'bold',
    },
    chartTitle: {
        textAlign: 'center',
        fontWeight: 'bold',
        marginBottom: '10px',
        fontSize: '1.1rem',
        color: theme.palette.text.primary,
    },
    legendContainer: {
        display: 'flex',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: '15px',
        marginBottom: '20px',
    },
    legendItem: {
        display: 'flex',
        alignItems: 'center',
        fontSize: '0.8rem',
        color: theme.palette.text.secondary,
    },
    legendColor: {
        width: '25px',
        height: '10px',
        marginRight: '8px',
        borderRadius: '2px',
    },
    calendarPicker: {
        backgroundColor: theme.palette.type === 'dark' ? '#333' : '#f0f0f0',
        borderRadius: '8px',
        padding: '6px 10px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px',
    },
    calendarField: {
        '& .MuiOutlinedInput-root': {
            backgroundColor: theme.palette.type === 'dark' ? '#2a3441' : '#ffffff',
            color: theme.palette.type === 'dark' ? '#ffffff' : '#111111',
            borderRadius: '6px',
        },
        '& .MuiOutlinedInput-input': {
            color: theme.palette.type === 'dark' ? '#ffffff' : '#111111',
            WebkitTextFillColor: theme.palette.type === 'dark' ? '#ffffff' : '#111111',
        },
        '& input[type="date"]': {
            color: theme.palette.type === 'dark' ? '#ffffff' : '#111111',
            WebkitTextFillColor: theme.palette.type === 'dark' ? '#ffffff' : '#111111',
            colorScheme: theme.palette.type === 'dark' ? 'dark' : 'light',
        },
        '& input[type="date"]::-webkit-datetime-edit': {
            color: theme.palette.type === 'dark' ? '#ffffff' : '#111111',
            WebkitTextFillColor: theme.palette.type === 'dark' ? '#ffffff' : '#111111',
        },
        '& input[type="date"]::-webkit-datetime-edit-fields-wrapper': {
            color: theme.palette.type === 'dark' ? '#ffffff' : '#111111',
            WebkitTextFillColor: theme.palette.type === 'dark' ? '#ffffff' : '#111111',
        },
        '& input[type="date"]::-webkit-datetime-edit-text': {
            color: theme.palette.type === 'dark' ? '#ffffff' : '#111111',
        },
        '& input[type="date"]::-webkit-datetime-edit-month-field': {
            color: theme.palette.type === 'dark' ? '#ffffff' : '#111111',
            WebkitTextFillColor: theme.palette.type === 'dark' ? '#ffffff' : '#111111',
        },
        '& input[type="date"]::-webkit-datetime-edit-day-field': {
            color: theme.palette.type === 'dark' ? '#ffffff' : '#111111',
            WebkitTextFillColor: theme.palette.type === 'dark' ? '#ffffff' : '#111111',
        },
        '& input[type="date"]::-webkit-datetime-edit-year-field': {
            color: theme.palette.type === 'dark' ? '#ffffff' : '#111111',
            WebkitTextFillColor: theme.palette.type === 'dark' ? '#ffffff' : '#111111',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.type === 'dark' ? '#036dda' : '#c7d9e5',
        },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#036dda',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#036dda',
        },
        '& input[type="date"]::-webkit-calendar-picker-indicator': {
            filter: theme.palette.type === 'dark'
                ? 'brightness(0) saturate(100%) invert(100%)'
                : 'brightness(0) saturate(100%) invert(0%)',
            opacity: 1,
        },
    },
    calendarInput: {
        color: theme.palette.text.primary,
    },
    selectInput: {
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.type === 'dark' ? '#333' : '#fff',
    },
    inputLabel: {
        color: theme.palette.text.secondary,
    }
}));

const AttendanceChart = () => {
    const classes = useStyles();
    const [arrivalData, setArrivalData] = useState([]);
    const [breakData, setBreakData] = useState([]);
    const [detailedData, setDetailedData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [activeTab, setActiveTab] = useState('arrival'); // 'arrival' or 'break'
    const [filterType, setFilterType] = useState('daily'); // 'daily', 'monthly', 'quarterly', 'yearly'

    useEffect(() => {
        const dateStr = selectedDate.toISOString().split('T')[0];
        fetchData(dateStr, filterType);
    }, [selectedDate, filterType]);

    const fetchData = async (date, type) => {
        setLoading(true);
        try {
            const response = await AuthApi.getDailyAttendanceStats(date, type);
            if (response && response.status) {
                setArrivalData(response.arrival_chart || []);
                setBreakData(response.break_chart || []);
                setDetailedData(response.data || []);
            } else {
                setArrivalData([]);
                setBreakData([]);
                setDetailedData([]);
            }
        } catch (error) {
            console.error("Error fetching chart data", error);
            setArrivalData([]);
            setBreakData([]);
            setDetailedData([]);
        } finally {
            setLoading(false);
        }
    };

    const getDayLabel = (date) => {
        const today = new Date();
        if (date.toDateString() === today.toDateString()) return 'TODAY';
        return date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
    };

    const formatTime12Hour = (timeStr) => {
        if (!timeStr) return '';
        try {
            if (timeStr.toLowerCase().includes('am') || timeStr.toLowerCase().includes('pm')) return timeStr;
            const [hours, minutes] = timeStr.split(':');
            if (hours === undefined || minutes === undefined) return timeStr;
            let h = parseInt(hours, 10);
            const m = minutes.substring(0, 2);
            const ampm = h >= 12 ? 'PM' : 'AM';
            h = h % 12;
            h = h ? h : 12;
            return `${h}:${m} ${ampm}`;
        } catch (e) { return timeStr; }
    };

    const getUsersForCategory = (category, type) => {
        if (!detailedData) return [];
        const normalizedCategory = category.toLowerCase().trim();
        if (type === 'arrival') {
            return detailedData
                .filter(user => (user.arrival?.status || '').toLowerCase().trim() === normalizedCategory)
                .map(user => {
                    const time = formatTime12Hour(user.arrival?.time || user.arrival?.punch_in || '');
                    return time ? `${user.name} (${time})` : user.name;
                });
        } else if (type === 'break') {
            return detailedData.flatMap(user => {
                const matchingBreaks = user.breaks?.filter(b => {
                    const status = (b.status || '').toLowerCase().trim();
                    const duration = parseInt(b.duration_minutes || 0, 10);

                    if (status) {
                        if (status === normalizedCategory) return true;
                        if (status.includes(normalizedCategory)) return true;
                        if (normalizedCategory.includes(status) && status.length > 3) return true;
                        if (normalizedCategory.includes('long') && status.includes('long')) return true;
                        if (normalizedCategory.includes('extended') && status.includes('extended')) return true;
                        if (normalizedCategory.includes('normal') && status.includes('normal')) return true;
                    }

                    if (normalizedCategory.includes('long') && duration > 120) return true;
                    if (normalizedCategory.includes('extended') && duration > 60 && duration <= 120) return true;
                    if (normalizedCategory.includes('normal') && duration > 0 && duration <= 60) return true;

                    return false;
                }) || [];

                return matchingBreaks.map(b => {
                    const start = formatTime12Hour(b.start_time || b.start || '');
                    const end = formatTime12Hour(b.end_time || b.end || '');
                    const timeInfo = start && end ? ` (${start} - ${end})` : (start ? ` (${start})` : '');
                    return `${user.name}${timeInfo}`;
                });
            });
        }
        return [];
    };

    const currentData = activeTab === 'arrival' ? arrivalData : breakData;
    const validData = currentData.filter(item => Number(item.value) > 0);

    const chartData = {
        labels: validData.map(item => item.name),
        datasets: [{
            data: validData.map(item => item.value),
            backgroundColor: validData.map(item => item.color),
            borderWidth: 0,
        }],
    };

    const chartOptions = {
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: 'rgba(33, 33, 33, 0.95)',
                padding: 15,
                cornerRadius: 8,
                callbacks: {
                    label: (context) => {
                        const value = context.raw || 0;
                        const total = context.chart._metasets[context.datasetIndex].total;
                        const percentage = ((value / total) * 100).toFixed(1) + '%';
                        return `${context.label}: ${value} (${percentage})`;
                    },
                    afterBody: (context) => {
                        const users = getUsersForCategory(context[0].label, activeTab);
                        const displayUsers = users.slice(0, 10);
                        const remaining = users.length - 10;
                        let text = users.length > 0 ? `\nUsers:\n• ${displayUsers.join('\n• ')}` : '';
                        if (remaining > 0) text += `\n...and ${remaining} more`;
                        return text;
                    }
                }
            }
        }
    };

    const renderDateSelector = () => {
        if (filterType === 'monthly') {
            // Show month/year selector
            const currentYear = selectedDate.getFullYear();
            const currentMonth = selectedDate.getMonth();
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

            return (
                <Box display="flex" justifyContent="center" gap={2} mb={3} flexWrap="wrap">
                    <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
                        <InputLabel className={classes.inputLabel}>Month</InputLabel>
                        <Select
                            value={currentMonth}
                            onChange={(e) => {
                                const newDate = new Date(selectedDate);
                                newDate.setMonth(e.target.value);
                                setSelectedDate(newDate);
                            }}
                            className={classes.selectInput}
                            label="Month"
                        >
                            {months.map((month, index) => (
                                <MenuItem key={index} value={index}>{month}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl variant="outlined" size="small" style={{ minWidth: 100 }}>
                        <InputLabel className={classes.inputLabel}>Year</InputLabel>
                        <Select
                            value={currentYear}
                            onChange={(e) => {
                                const newDate = new Date(selectedDate);
                                newDate.setFullYear(e.target.value);
                                setSelectedDate(newDate);
                            }}
                            className={classes.selectInput}
                            label="Year"
                        >
                            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                                <MenuItem key={year} value={year}>{year}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            );
        }

        if (filterType === 'quarterly') {
            // Show quarter/year selector
            const currentYear = selectedDate.getFullYear();
            const currentMonth = selectedDate.getMonth();
            const currentQuarter = Math.floor(currentMonth / 3) + 1;
            const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];

            return (
                <Box display="flex" justifyContent="center" gap={2} mb={3} flexWrap="wrap">
                    <FormControl variant="outlined" size="small" style={{ minWidth: 100 }}>
                        <InputLabel className={classes.inputLabel}>Quarter</InputLabel>
                        <Select
                            value={currentQuarter - 1}
                            onChange={(e) => {
                                const newDate = new Date(selectedDate);
                                const quarter = e.target.value + 1;
                                newDate.setMonth((quarter - 1) * 3);
                                newDate.setDate(1);
                                setSelectedDate(newDate);
                            }}
                            className={classes.selectInput}
                            label="Quarter"
                        >
                            {quarters.map((quarter, index) => (
                                <MenuItem key={index} value={index}>{quarter}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl variant="outlined" size="small" style={{ minWidth: 100 }}>
                        <InputLabel className={classes.inputLabel}>Year</InputLabel>
                        <Select
                            value={currentYear}
                            onChange={(e) => {
                                const newDate = new Date(selectedDate);
                                newDate.setFullYear(e.target.value);
                                setSelectedDate(newDate);
                            }}
                            className={classes.selectInput}
                            label="Year"
                        >
                            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                                <MenuItem key={year} value={year}>{year}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            );
        }

        if (filterType === 'yearly') {
            // Show year selector
            const currentYear = selectedDate.getFullYear();

            return (
                <Box display="flex" justifyContent="center" mb={3}>
                    <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
                        <InputLabel className={classes.inputLabel}>Year</InputLabel>
                        <Select
                            value={currentYear}
                            onChange={(e) => {
                                const newDate = new Date(selectedDate);
                                newDate.setFullYear(e.target.value);
                                newDate.setMonth(0);
                                newDate.setDate(1);
                                setSelectedDate(newDate);
                            }}
                            className={classes.selectInput}
                            label="Year"
                        >
                            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                                <MenuItem key={year} value={year}>{year}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            );
        }

        // Daily filter - Generate last 7 days
        const dates = [];
        for (let i = 6; i >= 0; i--) {
            const d = new Date(selectedDate);
            d.setDate(d.getDate() - i);
            dates.push(d);
        }

        return (
            <div className={classes.dateSelector}>
                {dates.map((date, index) => {
                    const isSelected = date.toDateString() === selectedDate.toDateString();
                    return (
                        <div key={index} onClick={() => setSelectedDate(date)}>
                            <div className={`${classes.dateCircle} ${isSelected ? classes.activeDateCircle : ''}`}>
                                {date.getDate()}
                            </div>
                            <div className={`${classes.dateLabel} ${isSelected ? classes.activeDateLabel : ''}`}>
                                {getDayLabel(date)}
                            </div>
                        </div>
                    );
                })}
                <div className={classes.calendarPicker}>
                    {/* <CalendarTodayIcon style={{ fontSize: '1rem', color: '#5bc0de' }} /> */}
                    <TextField
                        className={classes.calendarField}
                        type="date"
                        variant="outlined"
                        size="small"
                        value={selectedDate.toISOString().split('T')[0]}
                        onChange={(e) => {
                            if (e.target.value) {
                                setSelectedDate(new Date(e.target.value));
                            }
                        }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: new Date().toISOString().split('T')[0] }}
                        InputProps={{ className: classes.calendarInput }}
                    />
                </div>
            </div>
        );
    };

    return (
        <Card className={classes.card}>
            <Box className={`${classes.header} common-section-header`}>
                <Typography className={classes.headerTitle}>Employee Analytics</Typography>
            </Box>
            <CardContent>
                <div className={classes.toggleContainer}>
                    <Button
                        className={`${classes.toggleButton} ${activeTab === 'arrival' ? classes.activeButton : ''}`}
                        onClick={() => setActiveTab('arrival')}
                    >
                        Latecomers
                    </Button>
                    <Button
                        className={`${classes.toggleButton} ${activeTab === 'break' ? classes.activeButton : ''}`}
                        onClick={() => setActiveTab('break')}
                    >
                        Long Breaks
                    </Button>
                </div>

                <div className={classes.filterContainer}>
                    {['daily', 'monthly', 'quarterly', 'yearly'].map((type) => (
                        <Button
                            key={type}
                            variant="outlined"
                            className={`${classes.filterButton} ${filterType === type ? classes.activeFilterButton : ''}`}
                            onClick={() => setFilterType(type)}
                        >
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                        </Button>
                    ))}
                </div>

                {renderDateSelector()}

                <Typography className={classes.chartTitle}>
                    {activeTab === 'arrival'
                        ? `Most Frequent Latecomers - ${filterType.toUpperCase()}`
                        : `Break Duration Analysis - ${filterType.toUpperCase()}`}
                </Typography>

                <div className={classes.legendContainer}>
                    {validData.map((item, index) => (
                        <div key={index} className={classes.legendItem}>
                            <div className={classes.legendColor} style={{ backgroundColor: item.color }}></div>
                            <span>{item.name}</span>
                        </div>
                    ))}
                </div>

                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box height={300} position="relative">
                        {validData.length > 0 ? (
                            <Pie data={chartData} options={chartOptions} />
                        ) : (
                            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                                <Typography color="textSecondary">No data available</Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

export default AttendanceChart;
